import { ReactNode, useMemo, Fragment, memo } from 'react';
import styled from 'styled-components';
import { Editor } from '@tiptap/react';

import Tooltip from '@components/Tooltip/Tooltip';
import ClickableIcon from '@components/ClickableIcon/ClickableIcon';
import Separator from '@components/Separator/Separator';
import { t } from '../../services/i18n';

import { ToolbarProps, ToolbarButtonProps, ToolbarOptionType } from './types';

const ToolbarWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  background-color: ${(props) => props.theme.colors.grey050};
  border-bottom: 1px solid ${(props) => props.theme.colors.grey100};
  box-sizing: border-box;
  padding: 0 2px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
`;

const ToolbarButton = ({
  iconName,
  tooltipContent,
  tooltipSize,
  onClick,
  disabled,
}: ToolbarButtonProps) => {
  const handleClick = useMemo(() => onClick, [onClick]);
  return (
    <Tooltip content={tooltipContent} size={tooltipSize} disabled={disabled}>
      <span>
        <ClickableIcon
          disabled={disabled}
          name={iconName}
          width="16px"
          height="16px"
          color="grey800"
          onClick={handleClick}
        />
      </span>
    </Tooltip>
  );
};

const transformText = (
  editor: Editor,
  textTransformer: (text: string) => string
) => {
  const { from, to } = editor.state.selection;
  const transaction = editor.state.tr;
  transaction.doc.nodesBetween(from, to, (node, pos) => {
    if (node.isText) {
      const nodeFrom = Math.max(pos, from);
      const nodeTo = Math.min(pos + node.nodeSize, to);
      // Check if the selected portion of the text overlaps with the current node
      // (because the selected text can span across multiple nodes)
      // If it does, replace that portion with its uppercase version
      // The check for nodeFrom < nodeTo is to ensure that we only replace
      // the text content if there is actually a selected portion to transform
      if (nodeFrom < nodeTo) {
        const plainText = node.textBetween(nodeFrom - pos, nodeTo - pos);
        const newText = editor.schema.text(textTransformer(plainText));
        transaction.replaceWith(nodeFrom, nodeTo, newText);
      }
    }
  });
  // Commit the changes
  editor.view.dispatch(transaction);
};

const buildToolbar = (
  editor: Editor,
  tools: ToolbarOptionType[],
  disabled: boolean
) => {
  const TOOLBAR_OPTIONS: Record<ToolbarOptionType, ReactNode> = {
    uppercase: (
      <ToolbarButton
        iconName="UPPERCASE"
        tooltipContent={t(
          'common:toolbarButton.upperCase',
          'Changes the selected text to uppercase'
        )}
        tooltipSize="small"
        onClick={() => transformText(editor, (text) => text.toUpperCase())}
        disabled={disabled}
      />
    ),
    lowercase: (
      <ToolbarButton
        iconName="LOWERCASE"
        tooltipContent={t(
          'common:toolbarButton.lowerCase',
          'Changes the selected text to lowercase'
        )}
        tooltipSize="small"
        onClick={() => transformText(editor, (text) => text.toLowerCase())}
        disabled={disabled}
      />
    ),
    titlecase: (
      <ToolbarButton
        iconName="TITLECASE"
        tooltipContent={t(
          'common:toolbarButton.titleCase',
          'Changes the first letter of each word from the selected text to uppercase'
        )}
        tooltipSize="small"
        onClick={() =>
          transformText(editor, (text) =>
            text.toLowerCase().replace(/\b\w/g, (match) => match.toUpperCase())
          )
        }
        disabled={disabled}
      />
    ),
    undo: (
      <ToolbarButton
        iconName="UNDO"
        tooltipContent={t('common:toolbarButton.undo', 'Undo')}
        onClick={() => editor.chain().focus().undo().run()}
        disabled={disabled}
      />
    ),
    redo: (
      <ToolbarButton
        iconName="REDO"
        tooltipContent={t('common:toolbarButton.redo', 'Redo')}
        onClick={() => editor.chain().focus().redo().run()}
        disabled={disabled}
      />
    ),
    copy: (
      <ToolbarButton
        iconName="COPY"
        tooltipContent={t(
          'common:toolbarButton.copy',
          "Copies all the field's content to the clipboard"
        )}
        tooltipSize="small"
        onClick={() => navigator.clipboard.writeText(editor.getText())}
        disabled={disabled}
      />
    ),
    divider: <Separator orientation="vertical" height="18px" margin="0 5px" />,
  };

  return (
    <ToolbarWrapper aria-label="Toolbar for Text Editor">
      {tools.map((tool, i) => {
        if (TOOLBAR_OPTIONS[tool]) {
          return <Fragment key={i}>{TOOLBAR_OPTIONS[tool]}</Fragment>;
        }
      })}
    </ToolbarWrapper>
  );
};

const Toolbar = ({ editor, disabled = false, tools = [] }: ToolbarProps) => {
  if (!editor || tools.length === 0) return null;

  return buildToolbar(editor, tools, disabled);
};

export default memo(Toolbar);
